"use client";

import { useState, useTransition } from "react";
import Link from "next/link";
import { Loader2, MailCheck } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { requestPasswordReset } from "@/lib/api/password-reset-actions";

export function ForgotPasswordForm() {
  const [done, setDone] = useState(false);
  const [pending, startTransition] = useTransition();

  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const email = (form.elements.namedItem("email") as HTMLInputElement).value;
    const tenantSlug = (
      form.elements.namedItem("tenantSlug") as HTMLInputElement
    ).value;
    startTransition(async () => {
      // Anti-enumeration: the backend never reveals whether the tenant,
      // e-mail or account exists, and neither does this page — every
      // outcome (unknown e-mail, unknown tenant, inactive account, even a
      // network hiccup) ends on the exact same confirmation screen below.
      // Never branch the displayed copy on the result.
      await requestPasswordReset(email, tenantSlug);
      setDone(true);
    });
  }

  if (done) {
    return (
      <div className="space-y-4" role="status">
        <MailCheck className="size-8 text-success" />
        <h2 className="text-xl font-bold tracking-tight text-foreground">
          Sprawdź skrzynkę e-mail
        </h2>
        <p className="text-muted-foreground">
          Jeśli konto o tym adresie istnieje, wysłaliśmy na nie link do
          zresetowania hasła (dotarcie może potrwać do kilku minut). Link jest
          ważny godzinę.
        </p>
        <Link
          href="/login"
          className="inline-block font-medium text-primary underline-offset-4 hover:underline"
        >
          Przejdź do logowania
        </Link>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="space-y-2">
        <Label htmlFor="email">Adres e-mail</Label>
        <Input
          id="email"
          name="email"
          type="email"
          autoComplete="email"
          placeholder="jan.kowalski@firma.pl"
          required
          className="h-11"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="tenantSlug">Firma (opcjonalnie)</Label>
        {/* Same field as the login form — a URL-style slug, not an
            organization display name — so the backend can resolve which
            tenant's account to look up. */}
        <Input
          id="tenantSlug"
          name="tenantSlug"
          type="text"
          autoComplete="off"
          placeholder="identyfikator firmy"
          className="h-11"
        />
      </div>

      <Button
        type="submit"
        size="lg"
        disabled={pending}
        className="h-11 w-full gap-2 rounded-xl text-sm transition-all duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] active:scale-[0.99]"
      >
        {pending ? <Loader2 className="size-4 animate-spin" /> : null}
        {pending ? "Wysyłanie…" : "Wyślij link"}
      </Button>

      <p className="text-center text-sm text-muted-foreground">
        <Link
          href="/login"
          className="font-medium text-foreground underline-offset-4 transition-colors duration-300 hover:underline"
        >
          Wróć do logowania
        </Link>
      </p>
    </form>
  );
}
